"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CUSTOM_COMPONENT_TYPES = exports.CustomComponentsValidationError = exports.CustomComponentsListSchema = exports.CustomComponentSchema = exports.FaqBlockComponentSchema = exports.EmbedVideoComponentSchema = exports.CtaButtonComponentSchema = exports.CustomPropertyDeclarationSchema = void 0;
exports.assertCustomComponentsValid = assertCustomComponentsValid;
/**
 * sl-y0sj.6: Typed Custom-Components Registry — schema layer.
 *
 * Custom Apps can host opinionated, typed components in addition to the
 * snapshot table. The first three components ship with the registry:
 *
 *   - `cta_button`     — single call-to-action button.
 *   - `embed_video`    — YouTube/Vimeo embed (no arbitrary `<iframe>`).
 *   - `faq_block`      — Q&A list rendered as `<details>`/`<summary>`.
 *
 * Validation runs server-side in `CustomAppPublishService` before a
 * snapshot is frozen. Invalid configs reject the publish so the public
 * renderer can trust the snapshot shape — no XSS surface, no arbitrary
 * HTML, no `<script>` injection.
 *
 * Frontend imports the same schema from this package; the custom-app-viewer
 * bundle re-uses the discriminated-union type to render each component
 * (server-friendly markup, hydrated client-side).
 */
const zod_1 = require("zod");
const doc_node_model_1 = require("./doc-node-model");
const external_video_1 = require("./external-video");
const formula_1 = require("./formula");
const formula_evaluator_1 = require("./formula-evaluator");
const HEX_COLOR_RE = /^#(?:[0-9a-fA-F]{3}){1,2}$/;
const HEX_COLOR_OR_NAMED = zod_1.z.union([
    zod_1.z.string().regex(HEX_COLOR_RE, 'must be #rgb or #rrggbb hex color'),
    zod_1.z.enum(['brand', 'neutral', 'success', 'warning', 'danger']),
]);
const HTTPS_URL = zod_1.z
    .string()
    .min(1)
    .refine(v => {
    if (v.startsWith('/'))
        return /^\/[^\s]*$/.test(v);
    if (v.startsWith('mailto:'))
        return /^mailto:[^\s@]+@[^\s]+$/.test(v);
    if (v.startsWith('https://')) {
        try {
            new URL(v);
            return true;
        }
        catch {
            return false;
        }
    }
    return false;
}, 'must be https://, mailto:, or a site-relative path');
const CustomPropertiesFieldSchema = zod_1.z
    .object({
    customProperties: doc_node_model_1.CustomPropertyMapSchema.optional(),
})
    .partial();
exports.CustomPropertyDeclarationSchema = zod_1.z.object({
    name: doc_node_model_1.CustomPropertyNameSchema,
    label: zod_1.z.string().min(1).max(80),
    description: zod_1.z.string().max(240).optional(),
    syntax: doc_node_model_1.CssPropertySyntaxSchema.default('*'),
    defaultFormula: formula_1.FormulaSchema,
});
const CustomPropertyDeclarationsFieldSchema = zod_1.z
    .object({
    customPropertyDeclarations: zod_1.z
        .array(exports.CustomPropertyDeclarationSchema)
        .max(24)
        .optional(),
})
    .partial();
/**
 * `cta_button` — single call-to-action.
 */
exports.CtaButtonComponentSchema = zod_1.z
    .object({
    type: zod_1.z.literal('cta_button'),
    id: zod_1.z.string().min(1).max(64),
    text: zod_1.z.string().min(1).max(120),
    href: HTTPS_URL,
    variant: zod_1.z.enum(['primary', 'secondary', 'ghost']).default('primary'),
    color: HEX_COLOR_OR_NAMED.optional(),
    ariaLabel: zod_1.z.string().max(160).optional(),
})
    .merge(CustomPropertiesFieldSchema)
    .merge(CustomPropertyDeclarationsFieldSchema);
/**
 * `embed_video` — third-party video embed. The renderer only emits
 * provider-specific iframes (no arbitrary URL embedding) so we don't
 * expand the XSS surface to "any iframe with any sandbox".
 */
exports.EmbedVideoComponentSchema = zod_1.z
    .object({
    type: zod_1.z.literal('embed_video'),
    id: zod_1.z.string().min(1).max(64),
})
    .merge(external_video_1.ExternalVideoMetadataSchema)
    .merge(CustomPropertiesFieldSchema)
    .merge(CustomPropertyDeclarationsFieldSchema);
const FaqItemSchema = zod_1.z.object({
    q: zod_1.z.string().min(1).max(280),
    a: zod_1.z.string().min(1).max(2000),
});
/**
 * `faq_block` — Q&A list.
 */
exports.FaqBlockComponentSchema = zod_1.z
    .object({
    type: zod_1.z.literal('faq_block'),
    id: zod_1.z.string().min(1).max(64),
    heading: zod_1.z.string().max(160).optional(),
    items: zod_1.z.array(FaqItemSchema).min(1).max(50),
})
    .merge(CustomPropertiesFieldSchema)
    .merge(CustomPropertyDeclarationsFieldSchema);
/**
 * Discriminated union over every shipped component type. Adding a new
 * component = (1) define `<Name>ComponentSchema` above, (2) add it to
 * the union, (3) extend the renderer in `custom-app-viewer`.
 *
 * The discriminator is `type` so zod can pick the right schema in O(1).
 */
exports.CustomComponentSchema = zod_1.z.discriminatedUnion('type', [
    exports.CtaButtonComponentSchema,
    exports.EmbedVideoComponentSchema,
    exports.FaqBlockComponentSchema,
]);
/**
 * Snapshot field — list of components rendered in order. Capped to keep
 * pathological inputs out of public HTML and the materialized snapshot
 * size bounded.
 */
exports.CustomComponentsListSchema = zod_1.z
    .array(exports.CustomComponentSchema)
    .max(50);
/**
 * Component-id uniqueness invariant: two components with the same `id`
 * within a view collide on hash-anchor links and analytics. zod has no
 * native "unique-by-key" so the publish-validator runs this check after
 * shape validation.
 */
class CustomComponentsValidationError extends Error {
    constructor(message) {
        super(message);
        this.name = 'CustomComponentsValidationError';
    }
}
exports.CustomComponentsValidationError = CustomComponentsValidationError;
function assertCustomComponentsValid(list) {
    const seen = new Set();
    for (const c of list) {
        if (seen.has(c.id)) {
            throw new CustomComponentsValidationError(`duplicate custom-component id "${c.id}" — every component must have a unique id within a view`);
        }
        seen.add(c.id);
        if (c.type === 'embed_video' && !(0, external_video_1.isExternalVideoIdValid)(c)) {
            throw new CustomComponentsValidationError(`invalid ${c.provider} video id on component "${c.id}"`);
        }
        assertCustomPropertyDeclarationsMatch(c);
    }
}
function assertCustomPropertyDeclarationsMatch(component) {
    const declarations = component.customPropertyDeclarations ?? [];
    const declaredNames = new Set();
    for (const declaration of declarations) {
        if (declaredNames.has(declaration.name)) {
            throw new CustomComponentsValidationError(`duplicate custom-property declaration "${declaration.name}" in component "${component.id}"`);
        }
        try {
            (0, formula_evaluator_1.evaluateFormula)(declaration.defaultFormula, {
                data: { component },
            });
        }
        catch (err) {
            throw new CustomComponentsValidationError(`custom-property declaration "${declaration.name}" on component "${component.id}" has invalid defaultFormula: ${err instanceof Error ? err.message : String(err)}`);
        }
        declaredNames.add(declaration.name);
    }
    for (const name of Object.keys(component.customProperties ?? {})) {
        if (declarations.length > 0 && !declaredNames.has(name)) {
            throw new CustomComponentsValidationError(`custom property "${name}" on component "${component.id}" has no declaration`);
        }
    }
}
/**
 * Registry of known component types — used by the renderer to dispatch
 * to the right React component. Frontend imports this list to render an
 * "unknown component" placeholder for forward-compatibility (e.g. when
 * an older client sees a config produced by a newer server).
 */
exports.CUSTOM_COMPONENT_TYPES = [
    'cta_button',
    'embed_video',
    'faq_block',
];
